import AddRounded from '@mui/icons-material/AddRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import InventoryRounded from '@mui/icons-material/InventoryRounded';
import PostAddRounded from '@mui/icons-material/PostAddRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueries } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router';
import { ApiError } from '../../api/client';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Money } from '../../components/Money';
import { ContactPicker, type Contact } from '../../components/pickers';
import { EmptyState } from '../../components/states';
import { businessDate } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import {
  fetchGoodsReceipt,
  keys,
  purchaseApi,
  useExpenseAccounts,
  useGoodsReceipt,
  useGoodsReceipts,
  usePurchaseOrder,
  usePurchasesMutation,
  useTaxRates,
  type GoodsReceipt,
  type GoodsReceiptLine,
} from './api';
import { TaxSelect } from './components/items';
import { DecimalField, DiscountInput, DocHeader, Section, TotalsRows } from './components/ui';
import { todayIn } from './lib/dates';
import { normalize } from './lib/decimal';
import {
  billReceiptRow,
  billTotalsInput,
  emptyExpenseRow,
  toBillBody,
  validateBill,
  type BillExpenseRow,
  type BillHeader,
  type BillReceiptRow,
} from './lib/forms';
import { computeTotals } from './lib/totals';

interface SourceLine {
  receipt: GoodsReceipt;
  line: GoodsReceiptLine;
}

/** `/purchases/bills/new` — from an order (`?orderId=`), a receipt (`?receiptId=`) or a supplier. */
export function SupplierBillFormPage() {
  const [sp] = useSearchParams();
  const orderId = sp.get('orderId') ?? undefined;
  const receiptId = sp.get('receiptId') ?? undefined;
  const order = usePurchaseOrder(orderId);
  const receipt = useGoodsReceipt(receiptId);
  const fixed = order.data?.contact ?? receipt.data?.contact;
  if ((orderId && order.isLoading) || (receiptId && receipt.isLoading))
    return (
      <Box>
        <Skeleton width={260} height={56} />
        <Skeleton variant="rounded" height={320} sx={{ mt: 2 }} />
      </Box>
    );
  return (
    <BillEditor
      key={orderId ?? receiptId ?? 'free'}
      orderId={orderId}
      receiptId={receiptId}
      fixedSupplier={fixed ? { id: fixed.id, name: fixed.name } : null}
      sourceLabel={order.data?.number ?? receipt.data?.number}
    />
  );
}

function BillEditor({
  orderId,
  receiptId,
  fixedSupplier,
  sourceLabel,
}: {
  orderId?: string;
  receiptId?: string;
  fixedSupplier: { id: string; name: string } | null;
  sourceLabel?: string;
}) {
  const { settings, format } = useShop();
  const navigate = useNavigate();
  const notify = useNotify();
  const taxEnabled = settings?.taxEnabled ?? false;
  const taxRates = useTaxRates();
  const accounts = useExpenseAccounts();
  const [supplier, setSupplier] = useState<{ id: string; name: string } | null>(fixedSupplier);
  const [header, setHeader] = useState<Omit<BillHeader, 'contactId'>>(() => ({
    supplierInvoiceNumber: '',
    docDate: todayIn(format.timeZone),
    dueDate: '',
    reference: '',
    notes: '',
    discountType: 'AMOUNT',
    discountValue: '',
  }));
  const [overrides, setOverrides] = useState<Record<string, Partial<BillReceiptRow>>>({});
  const [expenses, setExpenses] = useState<BillExpenseRow[]>([]);
  const [showErrors, setShowErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);

  // Receipts with lines to bill: the one given, the order's, or the supplier's.
  const list = useGoodsReceipts(
    orderId
      ? { purchaseOrderId: orderId, status: 'POSTED', pageSize: 100 }
      : { contactId: supplier?.id, status: 'POSTED', pageSize: 100 },
    !receiptId && (!!orderId || !!supplier),
  );
  const receiptIds = receiptId ? [receiptId] : (list.data?.data.map((r) => r.id) ?? []);
  const details = useQueries({
    queries: receiptIds.map((id) => ({
      queryKey: keys.receipt(id),
      queryFn: () => fetchGoodsReceipt(id),
    })),
    combine: (rs) => ({
      loading: rs.some((r) => r.isLoading),
      receipts: rs.flatMap((r) => (r.data ? [r.data] : [])),
    }),
  });
  const loadingLines = list.isLoading || details.loading;
  const sources: SourceLine[] = details.receipts
    .flatMap((receipt) => receipt.lines.map((line) => ({ receipt, line })))
    .filter(({ receipt, line }) => receipt.status === 'POSTED' && Number(line.qtyToBill) > 0);

  // Order tax rates per order line (the bill's default when the rate isn't changed).
  const orderIds = [
    ...new Set(
      sources.flatMap((s) => (s.receipt.purchaseOrderId ? [s.receipt.purchaseOrderId] : [])),
    ),
  ];
  const orderLineRate = useQueries({
    queries: orderIds.map((id) => ({
      queryKey: keys.order(id),
      queryFn: () => purchaseApi.getOrder(id),
      staleTime: 60_000,
    })),
    combine: (rs) => {
      const m = new Map<string, string>();
      for (const r of rs) for (const l of r.data?.lines ?? []) m.set(l.id, l.taxRate);
      return m;
    },
  });

  const rows: BillReceiptRow[] = sources.map(({ line }) => ({
    ...billReceiptRow(line),
    ...overrides[line.id],
  }));
  const sourceById = new Map(sources.map((s) => [s.line.id, s]));
  const rates = useMemo(
    () => new Map((taxRates.data ?? []).map((r) => [r.id, r.rate])),
    [taxRates.data],
  );

  const totals = computeTotals(
    billTotalsInput(rows, expenses, {
      source: (id) => sourceById.get(id)?.line,
      rateOf: (id) => rates.get(id),
      defaultRateOf: (id) => {
        const pol = sourceById.get(id)?.line.purchaseOrderLineId;
        return pol ? orderLineRate.get(pol) : undefined;
      },
      decimals: format.currencyDecimals,
    }),
    {
      decimals: format.currencyDecimals,
      taxEnabled,
      pricesIncludeTax: false,
      documentDiscount:
        header.discountValue && /^\d/.test(header.discountValue)
          ? { type: header.discountType, value: header.discountValue }
          : null,
    },
  );
  const included = rows.filter((r) => r.include);
  const lineTotal = (i: number) => totals.lines[i]?.total ?? '0';
  // totals.lines follow [included receipt rows..., expenses...]
  const includedIndex = new Map(included.map((r, i) => [r.goodsReceiptLineId, i]));

  const fullHeader: BillHeader = { ...header, contactId: supplier?.id ?? '' };
  const errors = { ...validateBill(fullHeader, rows, expenses), ...serverErrors };
  const err = (k: string) => (showErrors ? errors[k] : undefined);

  const post = usePurchasesMutation(() =>
    purchaseApi.createBill(toBillBody(fullHeader, rows, expenses, { taxEnabled })),
  );

  const setRow = (id: string, p: Partial<BillReceiptRow>) =>
    setOverrides((o) => ({ ...o, [id]: { ...o[id], ...p } }));
  const setExpense = (key: string, p: Partial<BillExpenseRow>) =>
    setExpenses((xs) => xs.map((x) => (x.key === key ? { ...x, ...p } : x)));

  // A new supplier starts a fresh selection.
  const lastSupplier = useRef(supplier?.id);
  useEffect(() => {
    if (lastSupplier.current === supplier?.id) return;
    lastSupplier.current = supplier?.id;
    setOverrides({});
  }, [supplier?.id]);

  const tryPost = () => {
    setShowErrors(true);
    setServerErrors({});
    const e = validateBill(fullHeader, rows, expenses);
    if (Object.keys(e).length) return;
    setConfirming(true);
  };

  const groups = new Map<string, { receipt: GoodsReceipt; rows: BillReceiptRow[] }>();
  for (const r of rows) {
    const s = sourceById.get(r.goodsReceiptLineId);
    if (!s) continue;
    const entry = groups.get(s.receipt.id) ?? { receipt: s.receipt, rows: [] };
    entry.rows.push(r);
    groups.set(s.receipt.id, entry);
  }
  const grouped = [...groups.values()];

  const allIncluded = rows.length > 0 && rows.every((r) => r.include);
  const canExpense = accounts.data !== undefined;

  return (
    <Box>
      <DocHeader
        back={
          orderId
            ? { to: `/purchases/orders/${orderId}`, label: sourceLabel ?? 'Purchase order' }
            : receiptId
              ? { to: `/purchases/receipts/${receiptId}`, label: sourceLabel ?? 'Goods receipt' }
              : { to: '/purchases?tab=bills', label: 'Purchases' }
        }
        eyebrow="Record supplier bill"
        title={supplier ? `Bill from ${supplier.name}` : 'Supplier bill'}
        subtitle="Copy the supplier’s invoice: what they charged for the goods you received, plus any extra costs."
      />

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1fr) 340px' },
          alignItems: 'start',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Card sx={{ p: 2.5, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Invoice details
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: '1fr 1fr', md: '2fr 1fr 1fr' },
              }}
            >
              <Box sx={{ gridColumn: { sm: '1 / -1', md: 'auto' } }}>
                {fixedSupplier ? (
                  <TextField label="Supplier" value={fixedSupplier.name} disabled fullWidth />
                ) : (
                  <ContactPicker
                    type="supplier"
                    value={supplier as Contact | null}
                    onChange={(c) => setSupplier(c ? { id: c.id, name: c.name } : null)}
                    error={!!err('header.contactId')}
                    helperText={err('header.contactId')}
                  />
                )}
              </Box>
              <TextField
                label="Supplier invoice #"
                value={header.supplierInvoiceNumber}
                onChange={(e) =>
                  setHeader((h) => ({ ...h, supplierInvoiceNumber: e.target.value }))
                }
                error={!!err('header.supplierInvoiceNumber')}
                helperText={err('header.supplierInvoiceNumber')}
                slotProps={{ htmlInput: { maxLength: 100 } }}
              />
              <TextField
                label="Reference"
                value={header.reference}
                onChange={(e) => setHeader((h) => ({ ...h, reference: e.target.value }))}
                slotProps={{ htmlInput: { maxLength: 100 } }}
              />
              <TextField
                type="date"
                label="Bill date"
                value={header.docDate}
                onChange={(e) => setHeader((h) => ({ ...h, docDate: e.target.value }))}
                error={!!err('header.docDate')}
                helperText={err('header.docDate')}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                type="date"
                label="Due date"
                value={header.dueDate}
                onChange={(e) => setHeader((h) => ({ ...h, dueDate: e.target.value }))}
                error={!!err('header.dueDate')}
                helperText={err('header.dueDate') ?? 'Blank = supplier’s payment terms'}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Box>
          </Card>

          <Section
            dense
            title="Received goods to bill"
            action={
              rows.length > 0 && (
                <Button
                  size="small"
                  color="inherit"
                  onClick={() =>
                    setOverrides((o) => {
                      const next = { ...o };
                      for (const r of rows)
                        next[r.goodsReceiptLineId] = {
                          ...next[r.goodsReceiptLineId],
                          include: !allIncluded,
                        };
                      return next;
                    })
                  }
                >
                  {allIncluded ? 'Select none' : 'Select all'}
                </Button>
              )
            }
          >
            {!supplier && !orderId && !receiptId ? (
              <Typography color="text.secondary" sx={{ p: 2.5 }}>
                Choose the supplier to see what you received from them and haven’t been billed for.
              </Typography>
            ) : loadingLines ? (
              <Box sx={{ p: 2.5 }}>
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} height={48} />
                ))}
              </Box>
            ) : rows.length === 0 ? (
              <EmptyState
                icon={<InventoryRounded />}
                title="Nothing waiting to be billed"
                body="Everything received from this supplier is billed already. You can still bill other costs below."
              />
            ) : (
              grouped.map((g) => (
                <Box key={g.receipt.id}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      px: 2.5,
                      py: 1,
                      bgcolor: 'action.hover',
                      alignItems: 'baseline',
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, textDecoration: 'none', color: 'text.primary' }}
                      component={RouterLink}
                      to={`/purchases/receipts/${g.receipt.id}`}
                    >
                      {g.receipt.number}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      received {businessDate(g.receipt.docDate, format)}
                      {g.receipt.supplierRef ? ` · DN ${g.receipt.supplierRef}` : ''}
                    </Typography>
                  </Stack>
                  {g.rows.map((r) => {
                    const s = sourceById.get(r.goodsReceiptLineId);
                    if (!s) return null;
                    const k = `r.${r.goodsReceiptLineId}`;
                    const idx = includedIndex.get(r.goodsReceiptLineId);
                    return (
                      <Box
                        key={r.goodsReceiptLineId}
                        sx={{
                          px: 2.5,
                          py: 1.75,
                          borderTop: 1,
                          borderColor: 'divider',
                          opacity: r.include ? 1 : 0.55,
                          transition: 'opacity 150ms',
                        }}
                      >
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                          <Checkbox
                            checked={r.include}
                            onChange={(e) =>
                              setRow(r.goodsReceiptLineId, { include: e.target.checked })
                            }
                            slotProps={{ input: { 'aria-label': `Bill ${s.line.description}` } }}
                            sx={{ ml: -1 }}
                          />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                              {s.line.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {normalize(r.maxQty)} {s.line.unitSymbol} to bill · received at{' '}
                              <Money value={r.defaultPrice} />/{s.line.unitSymbol}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontWeight: 650, whiteSpace: 'nowrap' }}>
                            {idx === undefined ? '—' : <Money value={lineTotal(idx)} />}
                          </Typography>
                        </Stack>
                        {r.include && (
                          <Box
                            sx={{
                              mt: 1.5,
                              pl: { md: 4.5 },
                              display: 'grid',
                              gap: 1.5,
                              alignItems: 'start',
                              gridTemplateColumns: {
                                xs: 'repeat(2, minmax(0,1fr))',
                                md: taxEnabled ? '0.8fr 1fr 1.3fr 1fr' : '0.8fr 1fr 1.3fr',
                              },
                            }}
                          >
                            <DecimalField
                              size="small"
                              label={`Qty (${s.line.unitSymbol})`}
                              value={r.qty}
                              onChange={(e) =>
                                setRow(r.goodsReceiptLineId, { qty: e.target.value })
                              }
                              error={!!err(`${k}.qty`)}
                              helperText={err(`${k}.qty`)}
                            />
                            <DecimalField
                              size="small"
                              label="Unit price"
                              value={r.unitPrice}
                              onChange={(e) =>
                                setRow(r.goodsReceiptLineId, { unitPrice: e.target.value })
                              }
                              error={!!err(`${k}.unitPrice`)}
                              helperText={err(`${k}.unitPrice`)}
                            />
                            <DiscountInput
                              size="small"
                              type={r.discountType}
                              value={r.discountValue}
                              onType={(t) => setRow(r.goodsReceiptLineId, { discountType: t })}
                              onValue={(v) => setRow(r.goodsReceiptLineId, { discountValue: v })}
                              error={err(`${k}.discountValue`)}
                            />
                            {taxEnabled && (
                              <TaxSelect
                                rates={taxRates.data ?? []}
                                value={r.taxRateId}
                                includeDefault
                                onChange={(v) => setRow(r.goodsReceiptLineId, { taxRateId: v })}
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ))
            )}
          </Section>

          {(canExpense || expenses.length > 0) && (
            <Section
              dense
              title="Other costs"
              action={
                <Button
                  size="small"
                  startIcon={<AddRounded />}
                  onClick={() => setExpenses((xs) => [...xs, emptyExpenseRow()])}
                  disabled={accounts.data?.length === 0}
                >
                  Add cost
                </Button>
              }
            >
              {expenses.length === 0 ? (
                <Typography color="text.secondary" variant="body2" sx={{ p: 2.5 }}>
                  {accounts.data?.length === 0
                    ? 'Add an expense account such as “Freight inward” to your chart of accounts to bill other costs.'
                    : 'Freight, loading, packaging… anything on the invoice that isn’t stock.'}
                </Typography>
              ) : (
                expenses.map((x, i) => {
                  const k = `e.${x.key}`;
                  return (
                    <Box
                      key={x.key}
                      sx={{
                        px: 2.5,
                        py: 1.75,
                        borderTop: i ? 1 : 0,
                        borderColor: 'divider',
                        display: 'grid',
                        gap: 1.5,
                        alignItems: 'start',
                        gridTemplateColumns: {
                          xs: 'repeat(2, minmax(0,1fr))',
                          md: taxEnabled
                            ? '1.2fr 1.4fr 0.6fr 1fr 1fr minmax(96px,auto) 40px'
                            : '1.2fr 1.4fr 0.6fr 1fr minmax(96px,auto) 40px',
                        },
                      }}
                    >
                      <TextField
                        select
                        size="small"
                        label="Account"
                        value={x.accountId}
                        onChange={(e) => setExpense(x.key, { accountId: e.target.value })}
                        error={!!err(`${k}.accountId`)}
                        helperText={err(`${k}.accountId`)}
                      >
                        {(accounts.data ?? []).map((a) => (
                          <MenuItem key={a.id} value={a.id}>
                            {a.code} · {a.name}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        size="small"
                        label="Description"
                        value={x.description}
                        onChange={(e) => setExpense(x.key, { description: e.target.value })}
                        error={!!err(`${k}.description`)}
                        helperText={err(`${k}.description`)}
                        slotProps={{ htmlInput: { maxLength: 400 } }}
                      />
                      <DecimalField
                        size="small"
                        label="Qty"
                        value={x.qty}
                        onChange={(e) => setExpense(x.key, { qty: e.target.value })}
                        error={!!err(`${k}.qty`)}
                        helperText={err(`${k}.qty`)}
                      />
                      <DecimalField
                        size="small"
                        label="Amount"
                        value={x.unitPrice}
                        onChange={(e) => setExpense(x.key, { unitPrice: e.target.value })}
                        error={!!err(`${k}.unitPrice`)}
                        helperText={err(`${k}.unitPrice`)}
                      />
                      {taxEnabled && (
                        <TaxSelect
                          rates={taxRates.data ?? []}
                          value={x.taxRateId}
                          onChange={(v) => setExpense(x.key, { taxRateId: v })}
                        />
                      )}
                      <Typography sx={{ fontWeight: 650, textAlign: 'right', pt: 1 }}>
                        <Money value={lineTotal(included.length + i)} />
                      </Typography>
                      <Tooltip title="Remove">
                        <IconButton
                          aria-label="Remove cost"
                          size="small"
                          onClick={() => setExpenses((xs) => xs.filter((y) => y.key !== x.key))}
                          sx={{ mt: 0.5 }}
                        >
                          <DeleteOutlineRounded fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  );
                })
              )}
            </Section>
          )}

          <Card sx={{ p: 2.5 }}>
            <TextField
              label="Notes"
              value={header.notes}
              onChange={(e) => setHeader((h) => ({ ...h, notes: e.target.value }))}
              multiline
              minRows={2}
              fullWidth
              slotProps={{ htmlInput: { maxLength: 2000 } }}
            />
          </Card>
        </Box>

        <Card sx={{ p: 2.5, position: { lg: 'sticky' }, top: { lg: 88 } }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Bill total
          </Typography>
          <DiscountInput
            label="Bill discount"
            type={header.discountType}
            value={header.discountValue}
            onType={(t) => setHeader((h) => ({ ...h, discountType: t }))}
            onValue={(v) => setHeader((h) => ({ ...h, discountValue: v }))}
            error={err('header.discountValue') ?? totals.error}
          />
          <Box sx={{ mt: 2 }}>
            <TotalsRows
              rows={[
                { label: 'Subtotal', value: totals.subtotal },
                ...(Number(totals.discountTotal) > 0
                  ? [{ label: 'Discounts', value: totals.discountTotal, negative: true }]
                  : []),
                ...(taxEnabled ? [{ label: 'Tax', value: totals.taxTotal }] : []),
              ]}
              total={totals.total}
              totalLabel="You owe"
            />
          </Box>
          {showErrors && errors.lines && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {errors.lines}
            </Alert>
          )}
          <Divider sx={{ my: 2.5 }} />
          <Button
            fullWidth
            size="large"
            variant="contained"
            startIcon={<PostAddRounded />}
            loading={post.isPending}
            onClick={tryPost}
          >
            Post bill
          </Button>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
            Bills are posted straight away. A price different from the receipt cost is booked as a
            purchase price variance.
          </Typography>
        </Card>
      </Box>

      <ConfirmDialog
        open={confirming}
        title="Post this bill?"
        body={
          <>
            <Money value={totals.total} /> will be added to what you owe {supplier?.name}
            {header.supplierInvoiceNumber.trim()
              ? ` for their invoice ${header.supplierInvoiceNumber.trim()}`
              : ''}
            . To change it later you’ll have to cancel it.
          </>
        }
        confirmLabel="Post bill"
        onConfirm={() =>
          post
            .mutateAsync(undefined)
            .then((bill) => {
              notify.success(`${bill.number} posted`);
              void navigate(`/purchases/bills/${bill.id}`, { replace: true });
            })
            .catch((e: unknown) => {
              if (e instanceof ApiError && e.code === 'DUPLICATE_SUPPLIER_INVOICE')
                setServerErrors({
                  'header.supplierInvoiceNumber': 'Already used on another bill from this supplier',
                });
              notify.error(e);
            })
        }
        onClose={() => setConfirming(false)}
      />
    </Box>
  );
}
